// packages/ui/src/useDialogLayer.ts
'use client';

import { RefObject, useEffect, useRef } from 'react';
import { currentOpener, focusInitial, restoreFocus, trapTab } from './focus';
import { DialogEntry, isTopDialog, pushDialog, removeDialog } from './dialogStack';

/**
 * For an overlay that is not a Modal or Drawer (a custom picker, a command palette): while open it
 * joins the dialog stack, so a Drawer beneath it stops trapping Tab and ignores Escape, the page
 * stays scroll-locked, and Escape closes it first. With a `containerRef` it also takes initial
 * focus, traps Tab, and hands focus back to the opener on close.
 */
export function useDialogLayer(
  isOpen: boolean,
  onEscape: () => void,
  containerRef?: RefObject<HTMLElement | null>,
): void {
  const latest = useRef(onEscape);
  useEffect(() => {
    latest.current = onEscape;
  });

  // Read during render, before any child of this open can autoFocus and steal it.
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  if (isOpen && !wasOpen.current) openerRef.current = currentOpener();
  wasOpen.current = isOpen;

  useEffect(() => {
    if (!isOpen) return;
    const container = containerRef?.current ?? null;
    const entry: DialogEntry = { container };
    pushDialog(entry);
    if (container) focusInitial(container);

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isTopDialog(entry)) return;
      if (e.key === 'Escape') {
        if (e.defaultPrevented) return;
        e.preventDefault();
        latest.current();
      } else if (container) {
        trapTab(e, container);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      removeDialog(entry);
      if (container) restoreFocus(openerRef.current);
    };
    // containerRef is a ref object, stable by contract
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
}
